import {Component, Inject, OnInit} from '@angular/core';
import {UtilsService} from "../../shared/utils.service";
import {AppService} from "../../app.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {
  NetworkedUsersSelectorComponent
} from "../../shared/networked-users-selector/networked-users-selector.component";
import {SitesSelectorComponent} from "../../shared/sites-selector/sites-selector.component";
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import {AssetsService} from "../assets.service";
import {AssetTypesSelectorComponent} from "../../shared/asset-types-selector/asset-types-selector.component";
import {VendorsSelectorComponent} from "../../shared/vendors-selector/vendors-selector.component";
import {
  NetworkedAccountsSelectorComponent
} from "../../shared/networked-accounts-selector/networked-accounts-selector.component";
import {AssetsFilters} from "./assets-filter.interface";

@Component({
  selector: 'app-assets-filter',
  templateUrl: './assets-filter.component.html',
  styleUrls: ['./assets-filter.component.scss']
})
export class AssetsFilterComponent implements OnInit {
  filters: AssetsFilters = {
    archived: 'false',
    asset_type_ids: [],
    site_ids: [],
    vendor_ids: [],
    contractor_owner_ids: [],
    contractor_ids: [],
    user_ids: [],
    status: 'all',
    owner_type: 'all',
    condition_rating: 'all',
    is_visible: 'all',
    is_mobile: 'all',
    is_safety_critical: 'all',
    maintenance_record_due_status: 'all',
    date_type: 'maintenance_due_date',
    date_range: [],
    hide_sites: false,
    selector_mode: false
  };

  statusOptions = this.utils.toOptions(this.assetsService.STATUSES);
  conditionRatingOptions = this.utils.toOptions(
    this.assetsService.CONDITION_RATINGS
  );
  ownerTypeOptions = this.utils.toOptions(this.assetsService.OWNER_TYPES);

  faHardHat = faHardHat;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    public assetsService: AssetsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AssetsFilterComponent>
  ) {}

  ngOnInit() {
    this.filters.archived = this.data['archived'] || 'false';
    this.filters.user_ids = this.data['user_ids'] || [];
    this.filters.contractor_ids = this.data['contractor_ids'] || [];
    this.filters.status = this.data['status'] || 'all';
    this.filters.owner_type = this.data['owner_type'] || 'all';
    this.filters.condition_rating = this.data['condition_rating'] || 'all';
    this.filters.is_visible = this.data['is_visible'] || 'all';
    this.filters.is_mobile = this.data['is_mobile'] || 'all';
    this.filters.is_safety_critical = this.data['is_safety_critical'] || 'all';
    this.filters.maintenance_record_due_status = this.data['maintenance_record_due_status'] || 'all';
    this.filters.date_type = this.data['date_type'] || 'maintenance_due_date';
    this.filters.date_range = this.data['date_range'] || [];
    this.filters.asset_type_ids = this.data['asset_type_ids'] || [];
    this.filters.contractor_owner_ids = this.data['contractor_owner_ids'] || [];
    this.filters.vendor_ids = this.data['vendor_ids'] || [];
    this.filters.site_ids = this.data['site_ids'] || [];
    this.filters.hide_sites = this.data['hide_sites'] || false;
    this.filters.selector_mode = this.data['selector_mode'] || false;
  }

  /**
   * Sets the output date range based on the provided event value.
   *
   * @param event
   * @return {void} - No return value.
   */
  getOutputDateRange(event: any): void {
    this.filters.date_range = event;
  }

  /**
   * Select asset types and update the asset type IDs filters.
   *
   * @return {void}
   */
  onSelectAssetTypes(): void {
    this.utils.showComponentDialog(
      AssetTypesSelectorComponent,
      {
        selected: this.filters.asset_type_ids,
        multiple: true,
        required: false,
        can_create: false
      },
      {
        width: '1500px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.asset_type_ids = results;
      }
    );
  }

  /**
   * Select sites and update the site IDs filters.
   *
   * @return {void}
   */
  onSelectSites(): void {
    this.utils
      .showComponentDialog(
        SitesSelectorComponent,
        {
          selected: this.filters.site_ids,
          multiple: true
        },
        {
          width: '1200px'
        }
      )
      .then((results) => {
        if (typeof results !== 'undefined') {
          this.filters.site_ids = results;
        }
      });
  }

  /**
   * Select contractor owners and update the contractor owner IDs filters.
   *
   * @return {void}
   */
  onSelectContractorOwners(): void {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: this.filters.contractor_owner_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.contractor_owner_ids = results;
      }
    );
  }

  /**
   * Select vendors and update the vendor IDs filters.
   *
   * @return {void}
   */
  onSelectVendors(): void {
    this.utils.showComponentDialog(
      VendorsSelectorComponent,
      {
        selected: this.filters.vendor_ids,
        multiple: true,
        required: false
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.vendor_ids = results;
      }
    );
  }

  /**
   * Select networked users and update the user IDs filters.
   *
   * @return {void}
   */
  onSelectUsers(): void {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.filters.user_ids,
        multiple: true,
        selectedAccountId: this.app.account.id,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.user_ids = results;
      }
    );
  }

  /**
   * Select contractors and update the contractor IDs filters.
   *
   * @return {void}
   */
  onSelectContractors(): void {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: this.filters.contractor_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.contractor_ids = results;
      }
    );
  }

  /**
   * Clears the filters and closes the form.
   *
   * @return {void}
   */
  onClearAndClose(): void {
    this.filters.archived = 'false';
    this.filters.user_ids = [];
    this.filters.contractor_ids = [];
    this.filters.status = 'all';
    this.filters.owner_type = 'all';
    this.filters.condition_rating = 'all';
    this.filters.is_visible = 'all';
    this.filters.is_mobile = 'all';
    this.filters.is_safety_critical = 'all';
    this.filters.maintenance_record_due_status = 'all';
    this.filters.date_type = 'maintenance_due_date';
    this.filters.date_range = [];
    this.filters.asset_type_ids = [];
    this.filters.contractor_owner_ids = [];
    this.filters.vendor_ids = [];
    this.filters.site_ids = [];

    this.onSaveAndClose();
  }

  /**
   * Saves the filters and closes the dialog.
   *
   * @return {void}
   */
  onSaveAndClose(): void {
    this.dialogRef.close(this.filters);
  }

  /**
   * Closes the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Clears the date range for this filter.
   *
   * @return {void}
   */
  onClearDateRange(): void {
    this.filters.date_range = [];
  }
}
